import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, MessageSquare, MapPin, Truck, Clock,
  CheckCircle2, Send, Image as ImageIcon, Loader2, Star, Package, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const driverIcon = new L.DivIcon({
  html: `<div style="background:#f97316;width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;"><span style="transform:rotate(45deg);color:white;font-size:13px;">🚚</span></div>`,
  className: "customer-driver-marker",
  iconSize: [30, 30],
  iconAnchor: [15, 30],
});

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem("customer_token");
const fileUrl = (p) => !p ? null : p.startsWith("http") ? p : `${API}/api/files/${p}`;

const STATUS_LABELS = {
  pending_payment: { label: "Awaiting payment", color: "bg-amber-500" },
  confirmed: { label: "Finding a driver", color: "bg-blue-500" },
  assigned: { label: "Driver accepted", color: "bg-indigo-500" },
  en_route_to_pickup: { label: "Driver on the way", color: "bg-orange-500" },
  arrived: { label: "Driver at pickup", color: "bg-orange-500" },
  loaded: { label: "Loaded", color: "bg-orange-500" },
  in_transit: { label: "In transit", color: "bg-orange-500" },
  delivered: { label: "Delivered", color: "bg-green-500" },
  completed: { label: "Completed", color: "bg-green-600" },
  cancelled: { label: "Cancelled", color: "bg-red-500" },
};

function TimelineBar({ status }) {
  const steps = ["confirmed", "assigned", "en_route_to_pickup", "in_transit", "delivered"];
  const currentIdx = Math.max(0, steps.indexOf(status === "completed" ? "delivered" : status));
  return (
    <div className="flex items-center gap-1">
      {steps.map((s, i) => (
        <div key={s} className={`flex-1 h-1.5 rounded-full ${i <= currentIdx ? "bg-orange-500" : "bg-slate-200"}`} />
      ))}
    </div>
  );
}

function ChatPanel({ bookingId, customerId }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/chat/${bookingId}/messages`, {
        headers: { Authorization: `Bearer ${token()}` },
        validateStatus: () => true,
      });
      if (res.status < 200 || res.status >= 300) return;
      setMsgs(res.data?.messages || []);
    } catch {}
  }, [bookingId]);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [msgs]);

  const send = async (e) => {
    e?.preventDefault();
    const content = text.trim();
    if (!content) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/chat/${bookingId}/messages`,
        { message: content },
        { headers: { Authorization: `Bearer ${token()}` }, validateStatus: () => true }
      );
      if (res.status < 200 || res.status >= 300) {
        toast.error(res.data?.detail || "Could not send");
        return;
      }
      setText("");
      fetchMessages();
    } finally {
      setSending(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error("Image too large (max 6MB)"); return; }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const upRes = await axios.post(`${API}/api/chat/${bookingId}/upload`, fd, {
        headers: { Authorization: `Bearer ${token()}` },
        validateStatus: () => true,
      });
      const upData = upRes.data;
      if (upRes.status < 200 || upRes.status >= 300) { toast.error(upData?.detail || "Upload failed"); return; }
      await axios.post(`${API}/api/chat/${bookingId}/messages`,
        { image_url: upData.image_url },
        { headers: { Authorization: `Bearer ${token()}` }, validateStatus: () => true }
      );
      fetchMessages();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col" style={{ height: 480 }} data-testid="chat-panel">
      <div className="bg-slate-900 text-white px-4 py-3 flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-orange-400" />
        <p className="font-semibold text-sm">Chat with your driver</p>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-slate-50 p-3 space-y-2" data-testid="chat-messages">
        {msgs.length === 0 && (
          <div className="text-center text-xs text-slate-400 mt-8">
            No messages yet. Say hello to your driver!
          </div>
        )}
        {msgs.map(m => {
          const mine = m.sender_id === customerId;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                mine ? "bg-orange-500 text-white rounded-br-sm" : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"
              }`}>
                {!mine && <p className="text-[10px] font-semibold text-slate-400 mb-0.5">{m.sender_name || "Driver"}</p>}
                {m.image_url && (
                  <img src={fileUrl(m.image_url)} alt="chat" className="rounded-lg mb-1 max-w-full" />
                )}
                {m.message && <p className="whitespace-pre-wrap break-words">{m.message}</p>}
                <p className={`text-[10px] mt-0.5 ${mine ? "text-orange-100" : "text-slate-400"}`}>
                  {new Date(m.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="p-2 border-t border-slate-100 flex items-center gap-2 bg-white">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingImage}
          className="p-2 text-slate-500 hover:text-orange-500"
          data-testid="chat-image-btn"
          title="Send photo"
        >
          {uploadingImage ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          className="hidden"
          onChange={(e) => { uploadImage(e.target.files[0]); e.target.value = ""; }}
        />
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message"
          className="flex-1 h-10"
          data-testid="chat-input"
        />
        <Button
          type="submit"
          disabled={sending || !text.trim()}
          className="h-10 w-10 p-0 bg-orange-500 hover:bg-orange-600"
          data-testid="chat-send-btn"
        >
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}

function LiveTrackingMap({ bookingId, enabled }) {
  const [loc, setLoc] = useState(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const fetchLoc = async () => {
      try {
        const res = await axios.get(`${API}/api/customer/bookings/${bookingId}/driver-location`, {
          headers: { Authorization: `Bearer ${token()}` },
          validateStatus: () => true,
        });
        if (!cancelled && res.data?.available) setLoc(res.data);
      } catch {}
    };
    fetchLoc();
    const interval = setInterval(fetchLoc, 8000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [bookingId, enabled]);

  if (!enabled) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
        <MapPin className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="font-semibold text-slate-700">Live tracking not active yet</p>
        <p className="text-xs text-slate-500 mt-1">Starts when your driver is on the way.</p>
      </div>
    );
  }

  if (!loc) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 text-center" data-testid="tracking-waiting">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500 mx-auto mb-2" />
        <p className="text-sm text-slate-600">Waiting for driver location...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="tracking-map">
      <div className="px-4 py-2.5 bg-slate-900 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-xs font-semibold">Live location</span>
        </div>
        <span className="text-[10px] text-slate-400">
          Updated {loc.updated_at ? new Date(loc.updated_at).toLocaleTimeString() : "now"}
        </span>
      </div>
      <div style={{ height: 280 }}>
        <MapContainer center={[loc.lat, loc.lng]} zoom={14} style={{ height: "100%", width: "100%" }} key={`${loc.lat},${loc.lng}`}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={[loc.lat, loc.lng]} icon={driverIcon}>
            <Popup><strong>{loc.driver_name}</strong><br/>Your driver</Popup>
          </Marker>
        </MapContainer>
      </div>
    </div>
  );
}

export default function CustomerBookingDetailPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerId, setCustomerId] = useState(null);

  const load = useCallback(async () => {
    if (!token()) { navigate("/customer/login"); return; }
    try {
      const [meRes, bRes] = await Promise.all([
        axios.get(`${API}/api/customer/me`, { headers: { Authorization: `Bearer ${token()}` }, validateStatus: () => true }),
        axios.get(`${API}/api/customer/bookings/${bookingId}`, { headers: { Authorization: `Bearer ${token()}` }, validateStatus: () => true }),
      ]);
      if (meRes.status === 401) { localStorage.removeItem("customer_token"); navigate("/customer/login"); return; }
      if (bRes.status === 403) { toast.error("You don't have access to this booking"); navigate("/customer/dashboard"); return; }
      if (bRes.status === 404) { toast.error("Booking not found"); navigate("/customer/dashboard"); return; }
      setCustomerId(meRes.data?.id);
      setBooking(bRes.data);
    } catch {
      toast.error("Could not load booking");
    } finally {
      setLoading(false);
    }
  }, [bookingId, navigate]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!booking) return null;
  const status = STATUS_LABELS[booking.status] || { label: booking.status, color: "bg-slate-500" };

  return (
    <div className="min-h-screen bg-slate-50 pb-10" data-testid="customer-booking-detail">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/customer/dashboard" className="flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <p className="text-xs font-mono text-slate-400">#{booking.id.slice(0, 8).toUpperCase()}</p>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        {/* Status hero */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-xl p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wide">Status</p>
          <div className="flex items-center gap-2 mt-1 mb-3">
            <span className={`w-2.5 h-2.5 rounded-full ${status.color} animate-pulse`} />
            <p className="text-lg font-bold" data-testid="booking-status-label">{status.label}</p>
          </div>
          <TimelineBar status={booking.status} />
        </div>

        {/* Driver card */}
        {booking.driver ? (
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="driver-card">
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Your driver</p>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-slate-100 overflow-hidden flex items-center justify-center">
                {booking.driver.profile_picture ? (
                  <img src={fileUrl(booking.driver.profile_picture)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Truck className="w-6 h-6 text-slate-400" />
                )}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900">{booking.driver.name}</p>
                <p className="text-xs text-slate-500 capitalize">
                  {booking.driver.vehicle_type?.replace("_", " ")} ·
                  <Star className="w-3 h-3 inline text-amber-400 fill-amber-400 mx-1" />
                  {booking.driver.rating}
                </p>
              </div>
              <a
                href={`tel:${booking.driver.phone}`}
                className="w-10 h-10 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center text-white"
                data-testid="driver-call-btn"
              >
                <Phone className="w-4 h-4" />
              </a>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <Clock className="w-6 h-6 text-slate-300 mx-auto mb-1" />
            <p className="text-sm font-medium text-slate-700">Waiting for a driver to accept</p>
            <p className="text-xs text-slate-500">We&apos;re notifying nearby drivers now.</p>
          </div>
        )}

        {/* Live tracking */}
        <LiveTrackingMap bookingId={booking.id} enabled={booking.tracking_enabled} />

        {/* Chat */}
        {booking.chat_enabled ? (
          <ChatPanel bookingId={booking.id} customerId={customerId} />
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
            <MessageSquare className="w-6 h-6 text-slate-300 mx-auto mb-1" />
            <p className="text-sm font-medium text-slate-700">Chat unlocks once a driver accepts</p>
          </div>
        )}

        {/* Trip Details */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Trip details</p>
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5" />
              <div>
                <p className="text-xs text-slate-500">Pickup</p>
                <p className="font-medium text-slate-900">{booking.pickup_address}</p>
                <p className="text-xs text-slate-500">{booking.pickup_postcode}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500 mt-1.5" />
              <div>
                <p className="text-xs text-slate-500">Delivery</p>
                <p className="font-medium text-slate-900">{booking.delivery_address}</p>
                <p className="text-xs text-slate-500">{booking.delivery_postcode}</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-100 text-center">
            <div>
              <p className="text-[10px] text-slate-500 uppercase">Distance</p>
              <p className="font-semibold text-slate-900 text-sm">{booking.distance_miles} mi</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase">Item</p>
              <p className="font-semibold text-slate-900 text-sm capitalize">{booking.item_type}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase">Total</p>
              <p className="font-semibold text-slate-900 text-sm">&pound;{booking.total_cost?.toFixed(2)}</p>
            </div>
          </div>
          {booking.notes && (
            <div className="pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500">Notes</p>
              <p className="text-sm text-slate-700">{booking.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
